import { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { sendOrQueue } from '../../../utils/offlineWrite';
import { WRITE_KINDS } from '../../../utils/writeOutbox';
import { usePhase } from '../../../contexts/PhaseContext';
import { getCurrentAcademicYearId, onAcademicYearChanged } from '../../../utils/academicYearContext';
// import { UserContext } from '../../../contexts/UserContext'; // No longer needed
import { Box, Button, Typography, Grid, TextField, MenuItem, Paper, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Alert, Snackbar } from '@mui/material';

const makeEmptyStudent = () => ({
  rowId: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
  firstName: '',
  lastName: '',
  dob: '',
  sex: '',
  placeOfBirth: '',
  address: '',
  classId: '',
  // schoolId removed; backend will use req.user.schoolId

  isRepeater: false,
  photoUrl: '',
  guardianName: '',
  guardianPhone: '',
  guardianEmail: '',
  createdAt: new Date().toISOString(), // Auto-generated created at timestamp
  updatedAt: new Date().toISOString() // Auto-generated updated at timestamp
});

const normalizePhaseCodeValue = (value) => {
  if (value === null || value === undefined) return '';
  const str = value.toString().trim().toLowerCase();
  if (!str) return '';
  if (['nur_pri', 'nursery', 'nursery_primary', 'primary', 'basic'].includes(str)) return 'nur_pri';
  if (['sec', 'secondary', 'second_cycle', 'high_school', 'lycee', 'lycée'].includes(str)) return 'sec';
  return str;
};

const getStoredPhaseCode = () => {
  try {
    const stored =
      localStorage.getItem('schoolAdmin_activePhase') ||
      localStorage.getItem('activePhase') ||
      '';
    return normalizePhaseCodeValue(stored);
  } catch (_) {
    return '';
  }
};

const getStoredPhaseId = () => {
  try {
    const raw =
      localStorage.getItem('schoolAdmin_activePhaseId') ||
      localStorage.getItem('activePhaseId');
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  } catch (_) {
    return null;
  }
};

const getStoredAcademicYearId = () => {
  const current = getCurrentAcademicYearId();
  if (current) return String(current);
  const schoolId = localStorage.getItem('schoolId') || localStorage.getItem('activeSchoolId') || localStorage.getItem('scopeSchoolId') || '';
  const key = schoolId ? `${schoolId}:currentAcademicYearId` : 'currentAcademicYearId';
  const legacy = localStorage.getItem(key) || localStorage.getItem('currentAcademicYearId') || '';
  return legacy ? String(legacy) : '';
};

const MultiStudentAdmissionForm = ({ classes = [], initialStudents = [] }) => {
  const { t } = useTranslation();
  const { activePhase, activePhaseId } = usePhase();
  const [students, setStudents] = useState([makeEmptyStudent()]);
  const [phaseEventCode, setPhaseEventCode] = useState(getStoredPhaseCode);
  const [phaseEventId, setPhaseEventId] = useState(getStoredPhaseId);
  const [selectedAcademicYearId, setSelectedAcademicYearId] = useState(getStoredAcademicYearId);
  const resolvedPhaseCode = useMemo(() => normalizePhaseCodeValue(activePhase || phaseEventCode || getStoredPhaseCode()), [activePhase, phaseEventCode]);
  const resolvedPhaseId = useMemo(() => {
    const raw = activePhaseId || phaseEventId || getStoredPhaseId();
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  }, [activePhaseId, phaseEventId]);
  const resolvedAcademicYearId = useMemo(() => {
    if (!selectedAcademicYearId) return null;
    const parsed = Number(selectedAcademicYearId);
    return Number.isFinite(parsed) ? parsed : null;
  }, [selectedAcademicYearId]);
  const resolvedSchoolId = useMemo(() => {
    const candidates = [
      localStorage.getItem('schoolId'),
      localStorage.getItem('activeSchoolId'),
      localStorage.getItem('scopeSchoolId')
    ];
    const raw = candidates.find(Boolean) || '';
    if (!raw) return null;
    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : raw;
  }, []);
  useEffect(() => {
    const unsubscribe = onAcademicYearChanged((id) => {
      if (id) setSelectedAcademicYearId(String(id));
    });
    return () => unsubscribe?.();
  }, []);
  useEffect(() => {
    const handler = (event) => {
      const codeFromEvent = normalizePhaseCodeValue(event?.detail?.code || '');
      if (codeFromEvent) setPhaseEventCode(codeFromEvent);
      else setPhaseEventCode(getStoredPhaseCode());
      setPhaseEventId(getStoredPhaseId());
    };
    window.addEventListener('app:phase:changed', handler);
    return () => window.removeEventListener('app:phase:changed', handler);
  }, []);
  useEffect(() => {
    if (activePhase) setPhaseEventCode(normalizePhaseCodeValue(activePhase));
  }, [activePhase]);
  useEffect(() => {
    if (activePhaseId) {
      const parsed = Number(activePhaseId);
      if (Number.isFinite(parsed)) setPhaseEventId(parsed);
    }
  }, [activePhaseId]);
  useEffect(() => {
    const ayFromStorage = getStoredAcademicYearId();
    if (ayFromStorage && ayFromStorage !== selectedAcademicYearId) {
      setSelectedAcademicYearId(ayFromStorage);
    }
  }, [selectedAcademicYearId]);
  // Update students when initialStudents prop changes
  useEffect(() => {
    if (Array.isArray(initialStudents) && initialStudents.length > 0) {
      // Ensure each imported student has a stable rowId
      const withIds = initialStudents.map(s => (
        s && s.rowId ? s : { ...s, rowId: `${Date.now()}_${Math.random().toString(36).slice(2)}` }
      ));
      setStudents(withIds);
    }
    // Do not reset when initialStudents is empty to avoid wiping user typing
  }, [initialStudents]);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [validationDialog, setValidationDialog] = useState({
    open: false,
    title: '',
    message: '',
    severity: 'error'
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  // const userContext = useContext(UserContext); // No longer needed

  const handleChange = (idx, e) => {
    const { name, value, type, checked, files } = e.target;
    let newValue = type === 'checkbox' ? checked : (files ? files[0] : value);
    
    // Validate date format for DD-MM-YYYY
    if (name === 'dob' && value) {
      const dateRegex = /^\d{2}-\d{2}-\d{4}$/;
      if (!dateRegex.test(value) && value !== '') {
        // Allow typing but don't update if invalid format
        return;
      }
    }
    
    setStudents(currentStudents => currentStudents.map((s, i) =>
      i === idx ? { ...s, [name]: newValue } : s
    ));
    if (success) setSuccess(false);
  };

  const addStudent = () => {
    setStudents(prev => [...prev, makeEmptyStudent()]);
  };

  const removeStudent = idx => {
    setStudents(students.filter((_, i) => i !== idx));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(false);

    // Validate student data before submission (frontend quick check)
    const validation = validateStudents();
    if (validation && validation.isValid === false) {
      setValidationDialog({
        open: true,
        title: t('admissions.validation.title'),
        message: validation.message,
        severity: 'error'
      });
      setSubmitting(false);
      return;
    }

    try {
      // Ensure DD-MM-YYYY format for dates
      const processedStudents = students.map(student => ({
        ...student,
        dateOfBirth: student.dob, // Keep DD-MM-YYYY format from controlled dob
        admissionDate: new Date().toLocaleDateString('en-GB'), // DD-MM-YYYY
      }));
      const augmentedStudents = processedStudents.map((student) => ({
        ...student,
        phaseId: resolvedPhaseId || undefined,
        phaseCode: resolvedPhaseCode || undefined,
        academicYearId: resolvedAcademicYearId || undefined,
        schoolId: resolvedSchoolId || undefined
      }));
      const requestHeaders = {
        'Content-Type': 'application/json'
      };
      if (resolvedPhaseCode) requestHeaders['X-Active-Phase'] = resolvedPhaseCode;
      if (resolvedPhaseId) requestHeaders['X-Active-Phase-Id'] = String(resolvedPhaseId);
      if (resolvedAcademicYearId) requestHeaders['X-Academic-Year-Id'] = String(resolvedAcademicYearId);
      if (resolvedSchoolId) {
        requestHeaders['X-Scope-School-Id'] = String(resolvedSchoolId);
        requestHeaders['X-School-Id'] = String(resolvedSchoolId);
      }
      try {
        const { queued, result } = await sendOrQueue({
          kind: WRITE_KINDS.ADMISSION,
          endpoint: '/api/admissions/bulk',
          method: 'POST',
          headers: requestHeaders,
          body: {
            students: augmentedStudents,
            academicYearId: resolvedAcademicYearId || undefined,
            phaseId: resolvedPhaseId || undefined,
            phaseCode: resolvedPhaseCode || undefined,
            schoolId: resolvedSchoolId || undefined
          },
          label: t('admissions.submit.successTitle')
        });
        setValidationDialog({
          open: true,
          title: queued ? t('offlineWrites.queuedTitle') : t('admissions.submit.successTitle'),
          // Queued admissions are held on this device until reconnect — don't
          // report them as saved.
          message: queued
            ? t('offlineWrites.queued')
            : t('admissions.submit.successMessage', { count: result.savedCount || processedStudents.length }),
          severity: queued ? 'info' : 'success'
        });
        setStudents([makeEmptyStudent()]); // Reset form only on success
        setSuccess(true);
      } catch (error) {
        let errorMsg = t('admissions.submit.failed');
        // Show backend validation errors in a user-friendly way
        if (error && error.data) {
          if (typeof error.data === 'string') errorMsg = error.data;
          else if (error.data.details && Array.isArray(error.data.details)) {
            errorMsg = error.data.details.join('\n');
          } else if (error.data.details) {
            errorMsg = error.data.details;
          } else if (error.data.error) errorMsg = error.data.error;
          else if (error.data.message) errorMsg = error.data.message;
        } else if (error && error.message) {
          errorMsg = error.message;
        }
        setValidationDialog({
          open: true,
          title: t('admissions.submit.errorTitle'),
          message: errorMsg,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error('Error submitting admission:', error);
      let errorMsg = t('admissions.submit.failed');
      if (error.message) {
        errorMsg = error.message;
      }
      setValidationDialog({
        open: true,
        title: t('admissions.submit.errorTitle'),
        message: errorMsg,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  // const showSnackbar = (message, severity) => {
  //   setSnackbar({
  //     open: true,
  //     message,
  //     severity
  //   });
  // };

  const showValidationDialog = (title, message, severity) => {
    setValidationDialog({
      open: true,
      title,
      message,
      severity
    });
  };

  // Enhanced date validation with detailed error reporting
  const validateDate = (dateString) => {
    if (!dateString || typeof dateString !== 'string') {
      return { valid: false, error: t('admissions.validation.dateInvalid') };
    }
    
    // Basic format check: DD-MM-YYYY
    const regex = /^(\d{2})-(\d{2})-(\d{4})$/;
    if (!regex.test(dateString)) {
      return { valid: false, error: t('admissions.validation.dateInvalidFormat') };
    }
    
    // Parse the date components
    const parts = dateString.split('-');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);
    
    // Validate ranges
    if (day < 1 || day > 31) {
      return { valid: false, error: t('admissions.validation.dateInvalidDay', { day }) };
    }
    
    if (month < 1 || month > 12) {
      return { valid: false, error: t('admissions.validation.dateInvalidMonth', { month }) };
    }
    
    if (year < 1900 || year > 2100) {
      return { valid: false, error: t('admissions.validation.dateInvalidYear', { year }) };
    }
    
    // Check for valid month-day combinations
    const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
    
    // Handle leap years for February
    const isLeapYear = (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
    if (month === 2 && isLeapYear) {
      daysInMonth[1] = 29;
    }
    
    if (day > daysInMonth[month - 1]) {
      return { valid: false, error: t('admissions.validation.dateInvalidDayMonth', { day, month, maxDays: daysInMonth[month - 1] }) };
    }
    
    return { valid: true, error: null };
  };
  
  const getMonthName = (month) => {
    const months = [t('admissions.months.january'), t('admissions.months.february'), t('admissions.months.march'), t('admissions.months.april'), t('admissions.months.may'), t('admissions.months.june'), t('admissions.months.july'), t('admissions.months.august'), t('admissions.months.september'), t('admissions.months.october'), t('admissions.months.november'), t('admissions.months.december')];
    return months[month - 1];
  };

  // Validate all student data before submission with detailed error reporting
  const validateStudents = () => {
    const errors = [];
    
    if (students.length === 0) {
      errors.push(t('admissions.validation.noStudents'));
    }
    
    students.forEach((student, idx) => {
      const studentErrors = [];
      
      if (student.dob) {
        const dateValidation = validateDate(student.dob);
        if (!dateValidation.valid) {
          studentErrors.push(dateValidation.error);
        }
      } else {
        studentErrors.push(t('admissions.validation.dateOfBirthRequired'));
      }
      
      if (!student.firstName || student.firstName.trim() === '') {
        studentErrors.push(t('admissions.validation.firstNameRequired'));
      }
      
      if (!student.lastName || student.lastName.trim() === '') {
        studentErrors.push(t('admissions.validation.lastNameRequired'));
      }
      
      if (!student.classId) {
        studentErrors.push(t('admissions.validation.classRequired'));
      }
      
      if (!student.sex) {
        studentErrors.push(t('admissions.validation.sexRequired'));
      } else if (!['Male', 'Female'].includes(student.sex)) {
        studentErrors.push(t('admissions.validation.sexInvalid'));
      }
      
      if (studentErrors.length > 0) {
        errors.push(`${t('admissions.validation.studentNumber', { number: idx + 1 })} (${student.firstName || t('admissions.validation.unnamed')} ${student.lastName || ''}):\n  • ${studentErrors.join('\n  • ')}`);
      }
    });
    
    if (errors.length > 0) {
      const errorHeader = `${t('admissions.validation.failed')} - ${errors.length} ${t('admissions.validation.issuesFound', { count: errors.length })}:\n\n`;
      showValidationDialog(
        t('admissions.validation.title'),
        errorHeader + errors.join('\n\n'),
        'error'
      );
      return { isValid: false, message: errorHeader + errors.join('\n\n') };
    }
    
    return { isValid: true, message: '' };
  };
  
  // Helper validation functions
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  
  const isValidPhone = (phone) => {
    const phoneRegex = /^[+]?[-\d\s()]{7,15}$/;
    return phoneRegex.test(phone);
  };

  return (
    <form onSubmit={handleSubmit}>
      <Typography variant="h5" sx={{ mb: 2 }}>{t('admissions.form.bulkTitle')}</Typography>

      {success && <Typography color="success.main" sx={{ mb: 2 }}>{t('admissions.submit.successInline')}</Typography>}
      {error && <Typography color="error" sx={{ mt: 2, mb: 2 }}>{error}</Typography>}

      {students.map((student, idx) => (
        <Paper key={student.rowId || `student-${idx}`} sx={{ p: 2, mb: 3 }} elevation={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{t('admissions.form.studentNumberLabel', { number: idx + 1 })}</Typography>
            {students.length > 1 && (
              <Button color="error" onClick={() => removeStudent(idx)}>{t('admissions.form.remove')}</Button>
            )}
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField label={t('admissions.form.firstName')} name="firstName" value={student.firstName} onChange={e => handleChange(idx, e)} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label={t('admissions.form.lastName')} name="lastName" value={student.lastName} onChange={e => handleChange(idx, e)} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label={t('admissions.form.placeOfBirth')} name="placeOfBirth" value={student.placeOfBirth} onChange={e => handleChange(idx, e)} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label={t('admissions.form.dob')}
                name="dob"
                type="date"
                value={student.dob ? student.dob.split('-').reverse().join('-') : ''}
                onChange={e => {
                  const date = e.target.value;
                  if (date) {
                    // Convert from YYYY-MM-DD to DD-MM-YYYY
                    const [year, month, day] = date.split('-');
                    const formattedDate = `${day}-${month}-${year}`;
                    handleChange(idx, { target: { name: 'dob', value: formattedDate } });
                  } else {
                    handleChange(idx, { target: { name: 'dob', value: '' } });
                  }
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label={t('admissions.form.sex')} name="sex" value={student.sex} onChange={e => handleChange(idx, e)} fullWidth required>
                <MenuItem value="Male">{t('admissions.form.sexMale')}</MenuItem>
                <MenuItem value="Female">{t('admissions.form.sexFemale')}</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label={t('admissions.form.address')} name="address" value={student.address} onChange={e => handleChange(idx, e)} fullWidth />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField select label={t('admissions.form.class')} name="classId" value={student.classId} onChange={e => handleChange(idx, e)} fullWidth required>
                {(classes || []).map(cls => (
                  <MenuItem key={cls.id} value={cls.id}>{cls.name}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                {student.photoUrl && (
                  <Box sx={{ 
                    width: 100, 
                    height: 100, 
                    border: '1px solid #ccc', 
                    borderRadius: 1,
                    overflow: 'hidden',
                    mb: 1
                  }}>
                    <img 
                      src={typeof student.photoUrl === 'string' ? student.photoUrl : URL.createObjectURL(student.photoUrl)} 
                      alt={t('admissions.form.student')} 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                  </Box>
                )}
                <Button variant="outlined" component="label" fullWidth>
                  {student.photoUrl ? t('admissions.form.changePhoto') : t('admissions.form.uploadPhoto')}
                  <input 
                    type="file" 
                    hidden 
                    name="photoUrl" 
                    accept="image/*" 
                    onChange={e => handleChange(idx, e)} 
                  />
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                <input type="checkbox" name="isRepeater" checked={student.isRepeater} onChange={e => handleChange(idx, e)} />
                <Typography sx={{ ml: 1 }}>{t('admissions.form.isRepeater')}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12}><Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>{t('admissions.form.guardianInformation')}</Typography></Grid>
            <Grid item xs={12} sm={6}>
              <TextField label={t('admissions.form.guardianName')} name="guardianName" value={student.guardianName} onChange={e => handleChange(idx, e)} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label={t('admissions.form.guardianPhone')} name="guardianPhone" value={student.guardianPhone} onChange={e => handleChange(idx, e)} fullWidth required />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField label={t('admissions.form.guardianEmail')} name="guardianEmail" value={student.guardianEmail} onChange={e => handleChange(idx, e)} type="email" fullWidth sx={{ mb: 1 }} />
              <Typography variant="caption" color="info.main" sx={{ mb: 2, display: 'block' }}>
                {t('admissions.form.guardianEmailHelper')}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      ))}
      {/* Validation Dialog */}
      <Dialog
        open={validationDialog.open}
        onClose={() => setValidationDialog({ ...validationDialog, open: false })}
        aria-labelledby="validation-dialog-title"
        aria-describedby="validation-dialog-description"
      >
        <DialogTitle id="validation-dialog-title">
          {validationDialog.title}
        </DialogTitle>
        <DialogContent>
          <Alert severity={validationDialog.severity}>
            {validationDialog.message}
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationDialog({ ...validationDialog, open: false })}>
            {t('admissions.actions.close')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <Box sx={{ mt: 2 }}>
        <Button onClick={addStudent} sx={{ mr: 2 }}>{t('admissions.actions.addAnotherStudent')}</Button>
        <Button type="submit" variant="contained" color="primary" disabled={submitting}>{t('admissions.actions.submitAll')}</Button>
      </Box>
    </form>
  );
};

export default MultiStudentAdmissionForm;
